/* eslint-disable no-console */
import { AxiosError } from 'axios'
import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { Col, Container, Row } from 'react-grid-system'
import { useTranslation } from 'react-i18next'
import {
  matchPath,
  RouteComponentProps,
  StaticContext,
  useLocation
} from 'react-router'

import { DeletePAFModal } from './DeletePAFModal'
import { OpaCheckout } from './OpaCheckout'
import { LocalStorageOPAKeys } from './OpaCheckout.models'
import { StyledSpinnerContainer } from './OpaCheckout.styles'
import { ClinTheme } from '../../ClinTheme'
import { OpaExitPreventionModal } from './OpaExitPreventionModal/OpaExitPreventionModal'
import { OpaExitPreventionPromptModal } from './OpaExitPreventionModal/OpaExitPreventionPromptModal'
import { AnnounceMode } from '../../components/ClinAnnounceBar/ClinAnnounceBar'
import {
  NewFeatureElements,
  PageNames
} from '../../components/ClinNewFeatureTooltip/ClinNewFeatureTooltip.types'
import { ClinSpinner } from '../../components/ClinSpinner'
import { SortDirectionType } from '../../components/ClinTableOrderToggle/ClinTableOrderToggle'
import { defaultBasketDetails } from '../../constants'
import { useAppContext } from '../../context/app'
import { ActionType, useBasket } from '../../context/basket'
import { createAnnounceEvent } from '../../events/AnnounceEvent'
import { AssociatedPatientsSortField } from '../../features/AssociatedPatients/AssociatedPatients.model'
import useChangeBackgroundColor from '../../hooks/useChangeBackgroundColor/useChangeBackgroundColor'
import { useCheckUnableHomePage } from '../../hooks/useCheckUnableHomePage/useCheckUnableHomePage'
import { useEffectOnlyOnce } from '../../hooks/useEffectOnlyOnce/useEffectOnlyOnce'
import { useNewFeaturesList } from '../../hooks/useNewFeaturesList/useNewFeaturesList'
import { AnalyticsEvent } from '../../services/Analytics'
import { AnalyticsPageEvent } from '../../services/Analytics/AnalyticsPageEvent'
import analyticsServiceSingleton from '../../services/Analytics/initAnalytics'
import {
  AuthError,
  cancelGetOrderById,
  cancelGetPatientDefaultDetail,
  cancelGetProgramFromSearchIndexById,
  cancelUpdateOrder,
  getOrderById,
  getPatientDefaultDetail,
  getPatientDetail,
  getPatients,
  getPhysicianPatients,
  getProgramFromSearchIndexById,
  newUnvalidatedShippingAddress,
  updateOrder,
  updatePatient,
  updatePatientDefaultDetail
} from '../../services/ApiService'
import { showWootricSurvey } from '../../services/Wootric/showWootricSurvey'
import {
  OrderCancellationSaga,
  createOrderCancellationLogger,
  logOrderCancellationResult
} from '../../services/Saga'
import {
  CreateCdhShippingAddressDto,
  CreateShippingAddressDto,
  OrderDto,
  OrgAddressDto,
  PatientSummaryAssociatedToPhysiciansSearchDto,
  PatientSummarySearchDto,
  ProgramCatalogDto,
  UpdateBasketDetailsDto
} from '../../types/swaggerTypes'
import { getUserPhone } from '../../utils/getUserPhone'
import { useBeforeUnload } from '../../utils/useBeforeUnload'
import { useCountries } from '../../utils/useCountries'
import { useErrorMessage } from '../../utils/useErrorMessage'
import { useOnMount } from '../../utils/useOnMount'
import {
  saveLastUsedAddress,
  getLastUsedAddress
} from '../../utils/lastUsedAddressService'
import { returnDefaultBasketValues } from '../Basket/Basket'
import { CreateAddressModal } from '../CheckoutOptions/CreateAddressModal/CreateAddressModal'
import { RouteStateType } from '../PatientAccessForm/PatientAccessFormContainer'
import {
  OPAOrderTypes,
  PatientOrderStatus
} from '../Patients/PatientDashboard/Patient.model'
import { ModalMode } from '../PhysicianDetails/PhysicianDetailsContainer'

interface IOpaCheckoutRouteParams {
  orderId: string
}

interface LocationState {
  programId: string
  patientNumber?: string
  patientId?: number
  orderType?: string
  newFeature?: NewFeatureElements
  stockOrder?: boolean
  from?: string
}

interface IOpaCheckoutProps
  extends RouteComponentProps<
    IOpaCheckoutRouteParams,
    StaticContext,
    LocationState
  > {}

export interface IDeliveryData {
  deliveryRadioId: string
  recipientName: string
  recipientPhoneNumber?: string
  recipientEmail?: string
  shippingAddressId: string
  poNumber: string
}

export const OpaCheckoutContainer: FunctionComponent<IOpaCheckoutProps> = ({
  match,
  location,
  history
}) => {
  const { t } = useTranslation()
  const { countries } = useCountries()
  const {
    dispatch,
    supportContact,
    user,
    userRole,
    userDetails,
    institute,
    portfolioCountryCode,
    defaultShippingAddress
  } = useAppContext()

  // eslint-disable-line
  const [, basketDispatch] = useBasket()
  const patientCentricFlag = true
  const { newFeaturesList, displayNewFeature } = useNewFeaturesList(
    PageNames.OpaCheckout
  )
  const { redirectUrl } = useCheckUnableHomePage()

  const { orderId } = match.params

  const [programId, setProgramId] = useState<string>(
    history.location.state?.programId
  ) // || '2028' // for debug
  const [isOrderLoading, setIsOrderLoading] = useState<boolean>(true)
  const [isProgramLoading, setIsProgramLoading] = useState<boolean>(true)
  const [isDefaultDetailsLoading, setIsDefaultDetailsLoading] =
    useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isSubmittingSaveAndClose, setIsSubmittingSaveAndClose] =
    useState<boolean>(false)
  const [isSubmittingAddress, setIsSubmittingAddress] = useState<boolean>(false)
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
  const [displayEditMode, setDisplayEditMode] = useState<boolean>(false)
  const [order, setOrder] = useState<OrderDto>()
  // Order reads can 404 briefly right after creation while the Data Platform
  // catches up (observed gaps up to ~52s in UAT) - retry a few times before
  // treating it as a genuine failure. Timeouts are tracked so they can be
  // cleared if the user navigates away mid-retry.
  const orderReadRetryTimeoutIdsRef = useRef<ReturnType<typeof setTimeout>[]>(
    []
  )
  const [program, setProgram] = useState<ProgramCatalogDto>()
  const [selectedAddress, setSelectedAddress] = useState<
    OrgAddressDto | undefined
  >(defaultShippingAddress)
  const [physicianTitleAndFullName, setPhysicianTitleAndFullName] = useState<
    string | undefined
  >()
  const [patientDOB, setPatientDOB] = useState<string | undefined>()
  const [patientStatus, setPatientStatus] = useState<string | undefined>()

  const [patientOrderType, setPatientOrderType] = useState<string | undefined>()
  const [resetForm, setResetForm] = useState<boolean>(false)

  const [deliveryNote, setDeliveryNote] = useState<string>('')

  const [isDuplicatePoModalOpen, setIsDuplicatePoModalOpen] =
    useState<boolean>(false)
  const [duplicatePoData, setDuplicatePoData] = useState<{
    poNumber: string
    orderNumbers: string[]
  } | null>(null)
  const [isProcessingDuplicatePo, setIsProcessingDuplicatePo] =
    useState<boolean>(false)
  const [pendingPoNumber, setPendingPoNumber] = useState<string | undefined>()

  // Track original delivery details and whether they changed for Save & Close logic
  const [originalDeliveryData, setOriginalDeliveryData] = useState<
    | {
        shippingAddressId: string
        recipientName: string
        recipientEmail?: string
        recipientPhoneNumber?: string
        deliveryNote: string
      }
    | undefined
  >()
  const [hasDeliveryDetailsChanged, setHasDeliveryDetailsChanged] =
    useState<boolean>(false)

  const [physicianId, setPhysicianId] = useState<string | undefined>()
  const [patientNumber, setPatientNumber] = useState<string | undefined>()
  const [isNewShippingAddressOpen, setIsNewShippingAddressOpen] =
    useState<boolean>(false)
  const [deliveryAddresses, setDeliveryAddresses] = useState<OrgAddressDto[]>(
    []
  )
  const [errorVisible, setErrorVisible] = useState<boolean>(false)
  const [isSaved, setIsSaved] = useState<boolean>(false)
  const [newFeature, setNewFeature] = React.useState<NewFeatureElements>()
  const routeState = useLocation<RouteStateType>()
  const [isLogoClicked, setIsLogoClicked] = useState<boolean>(false)
  const [showExitPreventionModal, setShowExitPreventionModal] =
    useState<boolean>(false)
  const [confirmedNavigation, setConfirmedNavigation] = useState<boolean>(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [lastLocation, setLastLocation] = useState<any>(null)
  const [isPromptModalShown, setIsPromptModalShown] = useState<boolean>(false)

  const matchPatientDashboardAndPatientDetails =
    (routeState.state &&
      matchPath(routeState.state.from, {
        path: '/patients'
      })) ||
    routeState?.state?.from?.startsWith('/programs/my-physicians/')

  const defaultPatientsSearchParams: PatientSummaryAssociatedToPhysiciansSearchDto =
    {
      query: '',
      filter: {
        programs: [],
        patientStatuses: []
      },
      pagination: {
        skip: 0,
        take: 100
      },
      sorting: {
        sortBy: AssociatedPatientsSortField.Status,
        order: SortDirectionType.Ascending.toUpperCase()
      }
    }

  // Initialize with last used address if available, otherwise use default
  const getInitialShippingAddressId = () => {
    const lastUsedAddressId = getLastUsedAddress(user?.profile?.sub)
    return (
      lastUsedAddressId ??
      (defaultShippingAddress ? defaultShippingAddress.addressId : '0')
    )
  }

  const defaultDeliveryData: IDeliveryData = {
    deliveryRadioId: '2',
    recipientName: userDetails?.contactCard.name ?? '',
    poNumber: '',
    recipientEmail: userDetails?.contactCard.email
      ? userDetails?.contactCard.email
      : '',
    recipientPhoneNumber: getUserPhone(userDetails?.contactCard)
      ? getUserPhone(userDetails?.contactCard)
      : '',
    shippingAddressId: getInitialShippingAddressId()
  }
  const [deliveryData, setDeliveryData] =
    useState<IDeliveryData>(defaultDeliveryData)

  const [oldAddressId, setOldAddressId] = useState<string>(
    deliveryData.shippingAddressId
  )

  const [addressModalError, setAddressModalError] = useState<string>()

  const [deletePAFModalMode, setDeletePAFModalMode] = React.useState<
    ModalMode | undefined
  >()

  const [deletePAFError, setDeletePAFError] = React.useState<string>()

  //Passed down to OPAHeader to disable other buttons
  const [isCancelling, setIsCancelling] = useState<boolean>(false)

  const supportPhone: string =
    supportContact.csPhoneNumber ?? '+44 (0) 1932 824100'

  const handleProgramError = useErrorMessage(t('opa_checkout:program_error'))

  const handlePatientDefaultDetailsError = useErrorMessage(
    t('opa_checkout:program_error')
  )

  const handlePatientError = useErrorMessage(
    'There was an error fetching patient information'
  )

  const leaveMessage = t('opa_checkout:leave_message')
  useBeforeUnload(leaveMessage, !isSubmitted)

  useChangeBackgroundColor(ClinTheme.colors.lightGrey)

  const handleDeliveryDataChange = (value: IDeliveryData) => {
    setDeliveryData(value)
    setOldAddressId(value.shippingAddressId)

    if (physicianId && patientNumber && institute?.data?.instituteId) {
      updatePatientDefaultDetail(physicianId, patientNumber, {
        instituteId: institute.data?.instituteId.toString(),
        patientNumber,
        defaultRecipientName: value.recipientName,
        defaultShippingAddressId: ''
      })
        .then(() => {})
        .catch((error) =>
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `An error occurred submitting your patient details. ${error}`
            )
          )
        )
    }
  }

  const handleAddressChange = (address: OrgAddressDto) => {
    // Save as last used address for future orders
    saveLastUsedAddress(address.addressId, user?.profile?.sub)

    // Keep the old localStorage for backward compatibility during transition
    localStorage.setItem(
      'deliveryAddress',
      JSON.stringify({
        shippingAddressId: address.addressId
      })
    )
    setSelectedAddress(address)
    setDeliveryData({
      ...deliveryData,
      shippingAddressId: address.addressId
    })
  }

  const buttonZIndex = newFeaturesList[NewFeatureElements.EditButtonMaChekout]
    ? '999'
    : undefined

  const noteZIndex =
    newFeaturesList[NewFeatureElements.DeliveryNote] &&
    !newFeaturesList[NewFeatureElements.EditButtonMaChekout]
      ? '999'
      : undefined

  const checkboxShaddow =
    newFeaturesList[NewFeatureElements.DeliveryNote] &&
    !newFeaturesList[NewFeatureElements.EditButtonMaChekout]
      ? '0px 0px 20px 0px #FFF'
      : undefined

  useEffect(() => {
    return () => {
      orderReadRetryTimeoutIdsRef.current.forEach(clearTimeout)
    }
  }, [])

  useOnMount(() => {
    if (!orderId) {
      return
    }

    // 12 x 5s = 60s of retrying, chosen to cover the worst-case Data
    // Platform read-after-write gap seen in UAT (~52s) plus margin.
    const ORDER_READ_RETRY_DELAY_MS = 5000
    const ORDER_READ_MAX_ATTEMPTS = 12

    const loadOrder = (attempt: number) => {
      getOrderById(orderId)
        .then((response) => {
          setOrder(response.data)
          const orderDetails = response.data
          const orderItem = response.data.lines.filter(
            (x) => x.medicationFlag === 'Y'
          )[0]
          const physicianId = orderItem?.physician?.physicianId
          physicianId && setPhysicianId(physicianId?.toString())
          const patientNumber = orderItem.patientNumber

          // Always set programId from order if not already set or if order has a valid value
          if (!programId && orderItem?.programId) {
            setProgramId(String(orderItem.programId))
          } else if (!programId) {
            console.warn(
              `No program ID found in order ${orderId}. This may cause issues with order operations.`
            )
          }
          if (physicianId && patientNumber) {
            setIsDefaultDetailsLoading(true)
            getPatientDefaultDetail(patientNumber, physicianId.toString())
              .then((result) => {
                if (matchPatientDashboardAndPatientDetails) {
                  if (response.data.deliveryNote)
                    setDeliveryNote(response.data.deliveryNote)

                  // Priority order for address selection:
                  // 1. Last used address (new service)
                  // 2. Legacy localStorage (for backward compatibility)
                  // 3. Order's existing delivery address
                  // 4. Default shipping address from patient data
                  // 5. Institute's default shipping address
                  const lastUsedAddressId = getLastUsedAddress(
                    user?.profile?.sub
                  )
                  const savedDeliveryAddress =
                    localStorage.getItem('deliveryAddress')
                  const legacySavedAddressId = savedDeliveryAddress
                    ? JSON.parse(savedDeliveryAddress).shippingAddressId
                    : null

                  const prioritizedAddressId =
                    lastUsedAddressId ?? // Highest priority: last used address
                    legacySavedAddressId ?? // Fallback to legacy storage
                    (orderDetails?.deliverTo.addressId
                      ? orderDetails?.deliverTo.addressId
                      : result.data.defaultShippingAddressId
                      ? result.data.defaultShippingAddressId
                      : defaultShippingAddress?.addressId ?? '')

                  setDeliveryData({
                    ...deliveryData,
                    recipientName:
                      orderDetails?.deliverToContact ??
                      result.data.defaultRecipientName,
                    shippingAddressId: prioritizedAddressId,
                    recipientEmail:
                      orderDetails?.recipientEmail ??
                      userDetails?.contactCard.email ??
                      '',
                    recipientPhoneNumber: orderDetails?.recipientPhoneNumber
                      ? orderDetails?.recipientPhoneNumber
                      : getUserPhone(userDetails?.contactCard)
                      ? getUserPhone(userDetails?.contactCard)
                      : '',
                    poNumber:
                      orderDetails?.customerPoNumber ??
                      orderDetails?.orderNumber ??
                      ''
                  })
                } else {
                  // Priority order for address selection:
                  // 1. Last used address (new service)
                  // 2. Legacy localStorage (for backward compatibility)
                  // 3. Default shipping address from patient data
                  // 4. Current delivery data address
                  const lastUsedAddressId = getLastUsedAddress(
                    user?.profile?.sub
                  )
                  const savedDeliveryAddress =
                    localStorage.getItem('deliveryAddress')
                  const legacySavedAddressId = savedDeliveryAddress
                    ? JSON.parse(savedDeliveryAddress).shippingAddressId
                    : null

                  const prioritizedAddressId =
                    lastUsedAddressId ?? // Highest priority: last used address
                    legacySavedAddressId ?? // Fallback to legacy storage
                    result.data.defaultShippingAddressId ??
                    deliveryData.shippingAddressId

                  if (
                    result.data.defaultRecipientName &&
                    result.data.defaultShippingAddressId
                  ) {
                    setDeliveryData({
                      ...deliveryData,
                      recipientName: result.data.defaultRecipientName,
                      shippingAddressId: prioritizedAddressId
                    })
                  } else if (result.data.defaultShippingAddressId) {
                    setDeliveryData({
                      ...deliveryData,
                      shippingAddressId: prioritizedAddressId
                    })
                  } else if (result.data.defaultRecipientName) {
                    setDeliveryData({
                      ...deliveryData,
                      recipientName: result.data.defaultRecipientName,
                      shippingAddressId: prioritizedAddressId
                    })
                  } else {
                    // Always use prioritized address even if no default data
                    setDeliveryData({
                      ...deliveryData,
                      shippingAddressId: prioritizedAddressId
                    })
                  }
                }

                if (orderItem?.physician) {
                  let physicianName = orderItem.physician.physicianFullName
                  if (orderItem.physician.physicianTitle) {
                    physicianName = orderItem.physician?.physicianTitle.concat(
                      ' ',
                      orderItem.physician.physicianFullName
                    )
                  }
                  setPhysicianTitleAndFullName(physicianName)
                }

                setPhysicianId(physicianId.toString())
                setPatientNumber(patientNumber)
                if (patientCentricFlag) {
                  const defaultSearchParams: PatientSummarySearchDto = {
                    query: patientNumber ?? '',
                    filter: {
                      programs: [],
                      patientOrderStatuses: [],
                      physicianId: undefined,
                      showMyPatient: false,
                      showMyProgram: false
                    },
                    pagination: {
                      skip: 0,
                      take: 100
                    },
                    sorting: {
                      sortBy: 'patientOrderStatus',
                      order: SortDirectionType.Descending
                    }
                  }
                  getPatients(defaultSearchParams).then((result) => {
                    const patientSummary = result?.data.result.filter(
                      (item) => {
                        return item.patientNumber === patientNumber
                      }
                    )
                    if (patientSummary && patientSummary[0]) {
                      setPatientDOB(patientSummary[0].dateOfBirth)
                      setPatientStatus(patientSummary[0].patientOrderStatus)
                      setPatientOrderType(patientSummary[0].orderType)
                    }
                  })
                } else {
                  getPhysicianPatients(physicianId, defaultPatientsSearchParams)
                    .then((result) => {
                      const patientSummary = result?.data.result.filter(
                        (item) => {
                          return item.patientNumber === patientNumber
                        }
                      )
                      patientSummary &&
                        patientSummary[0]?.patientId &&
                        getPatientDetail(
                          patientSummary[0].patientId.toString(),
                          physicianId.toString()
                        ).then((result) => {
                          result?.data?.dateOfBirth &&
                            setPatientDOB(result.data.dateOfBirth)
                        })
                    })
                    .catch((error) => handlePatientError(error))
                }
              })
              .catch((error) => handlePatientDefaultDetailsError(error))
              .finally(() => setIsDefaultDetailsLoading(false))
          }
        })
        .catch((error: AxiosError) => {
          const isOrderNotYetReadable = error?.response?.status === 404
          if (isOrderNotYetReadable && attempt < ORDER_READ_MAX_ATTEMPTS) {
            orderReadRetryTimeoutIdsRef.current.push(
              setTimeout(
                () => loadOrder(attempt + 1),
                ORDER_READ_RETRY_DELAY_MS
              )
            )
            return
          }
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              t('opa_checkout:announce_customer_services_title', {
                supportPhone: supportPhone
              }),
              t('opa_checkout:announce_customer_services_description', {
                supportPhone: supportPhone
              })
            )
          )
        })
        .finally(() => setIsOrderLoading(false))
    }

    loadOrder(1)

    return () => {
      cancelGetOrderById()
      cancelGetProgramFromSearchIndexById()
      cancelGetPatientDefaultDetail()
      cancelUpdateOrder()
    }
  })

  // Capture initial delivery details after first load
  useEffect(() => {
    if (
      !originalDeliveryData &&
      order &&
      !isDefaultDetailsLoading &&
      !isOrderLoading
    ) {
      setOriginalDeliveryData({
        shippingAddressId: deliveryData.shippingAddressId ?? '',
        recipientName: deliveryData.recipientName ?? '',
        recipientEmail: deliveryData.recipientEmail ?? '',
        recipientPhoneNumber: deliveryData.recipientPhoneNumber ?? '',
        deliveryNote: deliveryNote ?? ''
      })
    }
  }, [
    order,
    originalDeliveryData,
    deliveryData,
    deliveryNote,
    isDefaultDetailsLoading,
    isOrderLoading
  ])

  // Detect changes in delivery details
  useEffect(() => {
    if (!originalDeliveryData) {
      setHasDeliveryDetailsChanged(false)
      return
    }

    const addressChanged =
      originalDeliveryData.shippingAddressId !== deliveryData.shippingAddressId
    const nameChanged =
      originalDeliveryData.recipientName !== (deliveryData.recipientName ?? '')
    const emailChanged =
      (originalDeliveryData.recipientEmail ?? '') !==
      (deliveryData.recipientEmail ?? '')
    const phoneChanged =
      (originalDeliveryData.recipientPhoneNumber ?? '') !==
      (deliveryData.recipientPhoneNumber ?? '')
    const noteChanged =
      (originalDeliveryData.deliveryNote ?? '') !== (deliveryNote ?? '')

    const changed =
      addressChanged ||
      nameChanged ||
      emailChanged ||
      phoneChanged ||
      noteChanged

    setHasDeliveryDetailsChanged(changed)
  }, [deliveryData, deliveryNote, originalDeliveryData])

  const handleDeliveryNote = () => {
    if (deliveryNote) {
      localStorage.setItem(LocalStorageOPAKeys.DeliveryNote, deliveryNote)
      analyticsServiceSingleton.trackEvent(AnalyticsEvent.AddDeliveryNote, {})
    } else {
      localStorage.setItem(LocalStorageOPAKeys.DeliveryNote, '')
    }
  }
  useEffect(() => {
    programId &&
      getProgramFromSearchIndexById(programId)
        .then((result) => {
          setProgram(result.data)
        })
        .catch((error) => handleProgramError(error))
        .finally(() => setIsProgramLoading(false))
  }, [programId])

  useEffect(() => {
    if (
      patientCentricFlag &&
      patientStatus &&
      patientStatus !== PatientOrderStatus.IncompleteCheckout
    ) {
      const patientId = history.location?.state?.patientId
      physicianId &&
        patientId &&
        updatePatient(physicianId, patientId, {
          orderStatus: PatientOrderStatus.IncompleteCheckout
        })
          .then(() => {})
          .catch((error: AxiosError) => {
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Error,
                `An error occurred updating patient status. ${error}`
              )
            )
          })
    }
  }, [physicianId, patientCentricFlag, patientStatus])

  // this useEffect need to redirect to the OpaOrderSummary page without a navigation guard when the order is booked
  useEffect(() => {
    if (isSubmitted) {
      if (order) {
        history.replace(`/opa-order-summary/${orderId}`, {
          programId: programId
        })
      } else {
        dispatch(
          createAnnounceEvent(
            AnnounceMode.Error,
            'There was an error placing order.'
          )
        )
      }
    } else if (isSaved) {
      const stockOrder =
        newFeature === NewFeatureElements.CancelCheckout
          ? history.location.state.stockOrder
          : undefined
      const redirectPrograms = '/programs/my-physicians/'
      const redirect = location?.state?.from?.startsWith(redirectPrograms)
        ? location.state.from
        : '/patients'

      history.push({
        pathname: redirect,
        state: {
          ...history.location?.state,
          programId: '',
          patientNumber:
            order?.lines.filter((x) => x.medicationFlag === 'Y')[0]
              .patientNumber ?? '',
          stockOrder: stockOrder,
          newFeature: newFeature
        }
      })
    }

    return () => {}
  }, [isSubmitted, order, history, orderId, programId, dispatch, isSaved])

  const handleSaveAndCloseButton = (
    programName?: string,
    programId?: string,
    poNumber?: string
  ) => {
    setConfirmedNavigation(true)
    window.scrollTo(0, 0)
    setIsSubmittingSaveAndClose(true)
    const patientId = history.location?.state?.patientId

    // CLOS-10882: Conditional updateOrder call based on delivery details changes
    if (hasDeliveryDetailsChanged) {
      updateOrder(orderId, order?.orderNumber!, {
        bookedFlag: 'N',
        shippingHold: 'N',
        deliverToContact: deliveryData.recipientName,
        custPONumber: poNumber ?? order?.orderNumber,
        recipientEmail: deliveryData.recipientEmail,
        recipientPhoneNumber: deliveryData.recipientPhoneNumber,
        deliveryNote: deliveryNote,
        shipToOrgId: !isNaN(parseInt(deliveryData.shippingAddressId))
          ? parseInt(deliveryData.shippingAddressId)
          : null
      })
        .then((response) => {
          if (response.status === 200) {
            setNewFeature(NewFeatureElements.ContinueCheckout)
            setIsSaved(true)
          } else {
            setIsSaved(false)
          }
          analyticsServiceSingleton.trackEvent(AnalyticsEvent.SaveAndClose, {
            location: 'ma checkout',
            orderId: orderId,
            programName: programName,
            programId: programId
          })
        })
        .catch((error) => {
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `An error occurred updating your order. ${error}`
            )
          )
        })
        .finally(() => setIsSubmittingSaveAndClose(false))
    } else if (physicianId && patientId) {
      updatePatient(physicianId, patientId, {
        orderStatus: PatientOrderStatus.IncompleteCheckout
      })
        .then(() => {
          setNewFeature(NewFeatureElements.ContinueCheckout)
          setIsSaved(true)
          analyticsServiceSingleton.trackEvent(AnalyticsEvent.SaveAndClose, {
            location: 'ma checkout',
            orderId: orderId,
            programName: programName,
            programId: programId
          })
        })
        .catch((error: AxiosError) => {
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `An error occurred updating patient status. ${error}`
            )
          )
        })
        .finally(() => setIsSubmittingSaveAndClose(false))
    } else {
      // Fallback: proceed without backend change if IDs are missing
      setNewFeature(NewFeatureElements.ContinueCheckout)
      setIsSaved(true)
      analyticsServiceSingleton.trackEvent(AnalyticsEvent.SaveAndClose, {
        location: 'ma checkout',
        orderId: orderId,
        programName: programName,
        programId: programId
      })
      setIsSubmittingSaveAndClose(false)
    }
  }

  const handleConfirmOrder = (poNumber: string | undefined) => {
    window.scrollTo(0, 0)
    setIsSubmitting(true)
    setPendingPoNumber(poNumber) // Store the PO number for potential retry
    attemptOrderConfirmation(poNumber, 'N')
  }

  // Attempts to confirm the order
  const attemptOrderConfirmation = (
    poNumber: string | undefined,
    duplicatePoCheck: string
  ) => {
    triggerDelayedError(15000)
    updateOrder(orderId, order?.orderNumber!, {
      bookedFlag: 'Y',
      shippingHold: 'Y',
      deliverToContact: deliveryData.recipientName,
      custPONumber: poNumber || order?.orderNumber,
      recipientEmail: deliveryData.recipientEmail,
      recipientPhoneNumber: deliveryData.recipientPhoneNumber,
      deliveryNote: deliveryNote,
      shipToOrgId: !isNaN(parseInt(deliveryData.shippingAddressId))
        ? parseInt(deliveryData.shippingAddressId)
        : null,
      duplicatePoCheck: 'N'
    })
      .then(({ data }) => {
        const patientId =
          data && data.lines.length > 0 ? data?.lines[0].patientNumber : 'N/A'
        analyticsServiceSingleton.trackEvent(AnalyticsEvent.OrderCompleted, {
          orderId: data?.orderNumber,
          order_number: data?.orderNumber, // for hubspot format
          division: 'MA',
          institute_id: user?.institute?.instituteId,
          has_hold: data ? data?.holds.length > 0 : false,
          currency: data?.currencyCode,
          order_value: data?.totals.total,
          patient_id: patientId
        })
        analyticsServiceSingleton.trackEvent(
          AnalyticsEvent.PurchaseOrderNumber,
          {}
        )
        setOrder(data)
        setIsSubmitted(true)
        let basketDetails: UpdateBasketDetailsDto = defaultBasketDetails
        if (userRole && userDetails)
          basketDetails = returnDefaultBasketValues(userRole, userDetails)
        basketDispatch({
          type: ActionType.UpdateBasketDetails,
          basketDetails: basketDetails
        })
        handleDeliveryNote()
        // Keep the last used address for future orders - don't clean up
        // Only clean up the legacy localStorage for backward compatibility
        localStorage.removeItem('deliveryAddress')
        // Trigger survey
        showWootricSurvey(user, order?.orderNumber, 'MA')
      })
      .catch((error: AxiosError) => {
        if (error.response?.status === 400 && duplicatePoCheck === 'Y') {
          let errorMessage =
            error.response?.data?.dependency?.response || error.message

          try {
            const parsed = JSON.parse(errorMessage)
            if (parsed?.dependency?.response) {
              errorMessage = parsed.dependency.response
            }
          } catch {}

          const poMatch = errorMessage.match(/The PO Number\s*\(?([^)]+)\)?/i)
          const ordersMatch = errorMessage.match(/orders?\s*\(([^)]+)\)/i)

          if (poMatch && ordersMatch) {
            const duplicatePoNumber = poMatch ? poMatch[1].trim() : null
            const orderNumbers = ordersMatch
              ? ordersMatch[1]
                  .split(',')
                  .map((id: string) => id.trim())
                  .filter(Boolean)
              : []

            setDuplicatePoData({ poNumber: duplicatePoNumber, orderNumbers })
            setIsSubmitting(false)
            return
          } else {
            setDuplicatePoData({
              poNumber: poNumber || 'n/a',
              orderNumbers: []
            })
            setIsSubmitting(false)
            return
          }
        }
        setIsSubmitting(false)
        setPendingPoNumber(undefined) // Clear pending PO on error (except duplicate PO case)
        dispatch(
          createAnnounceEvent(
            AnnounceMode.Error,
            `An error occurred submitting your order. ${error}`
          )
        )
        console.error('Submit Order Error', {
          message: error.message,
          code: error.code,
          isAxiosError: error.isAxiosError,
          response: error.response,
          status: error.response?.status,
          data: error.response?.data
        })
      })
      .finally(() => {
        if (!isDuplicatePoModalOpen) {
          setIsSubmitting(false)
        }
      })
  }

  // Handle duplicate PO confirmation
  const handleDuplicatePoConfirm = () => {
    setIsProcessingDuplicatePo(true)
    setIsDuplicatePoModalOpen(false)
    setIsSubmitting(true)
    // Use the PO number from the original submission attempt, not from deliveryData
    const currentPoNumber = pendingPoNumber || order?.orderNumber
    attemptOrderConfirmation(currentPoNumber, 'N')
  }

  // Handle duplicate PO cancel - return to checkout
  const handleDuplicatePoCancel = () => {
    setIsDuplicatePoModalOpen(false)
    setDuplicatePoData(null)
    setIsProcessingDuplicatePo(false)
    setIsSubmitting(false)
    setPendingPoNumber(undefined) // Clear the pending PO number
  }

  const getAddressResponseErrorMessage = (message: any) => {
    if (message) {
      const messageLine = message.split(',')
      if (messageLine && messageLine[6]) {
        const messageLineText = messageLine[6].split(':')
        if (messageLineText) {
          const errorMessage = messageLineText[2]
          if (errorMessage) {
            const errorMessageWithoutQuotations = errorMessage.split('"')
            if (errorMessageWithoutQuotations) {
              const finalErrorMessage = errorMessageWithoutQuotations[1]
              return finalErrorMessage ?? ''
            }
          }
        }

        return ''
      }
    }
  }

  const handleNewShippingAddress = (data: CreateShippingAddressDto) => {
    if (data && data.address) {
      setIsSubmittingAddress(true)
      const newAddress: CreateCdhShippingAddressDto = {
        address: {
          ...data.address,
          countryCode: data.address.country
        }
      }
      newUnvalidatedShippingAddress(newAddress)
        .then((response) => {
          if (response?.data) {
            const newAddress = response?.data
            if (newAddress) {
              const newDeliveryAddresses: OrgAddressDto[] =
                deliveryAddresses.slice()
              newDeliveryAddresses.unshift(newAddress)
              setDeliveryAddresses(newDeliveryAddresses)
              setSelectedAddress(newAddress)
              setDeliveryData({
                ...deliveryData,
                shippingAddressId: newAddress.addressId
              })
            }
            analyticsServiceSingleton.trackEvent(
              AnalyticsEvent.CreateDeliveryAddress,
              {}
            )
            setIsNewShippingAddressOpen(false)
            setAddressModalError('')
            setResetForm(true)
          }
        })
        .catch((error: AxiosError) => {
          const { code, message } = error
          setResetForm(false)
          // If request is cancelled continue
          if (error.message === AuthError.RequestCancelled) {
            return
          }
          // If we have a full error show it
          if (error.response && error.response.data) {
            setAddressModalError(
              getAddressResponseErrorMessage(
                error.response.data.dependency.response
              )
            )
          } else {
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Error,
                `${t(
                  'request_new_shipping_address:submitted_fail_message'
                )} ${message} ${code ?? ''}`
              )
            )
          }
        })
        .finally(() => {
          setIsSubmittingAddress(false)
        })
    }
  }

  const handleCancelButton = () => {
    setIsCancelling(true)
    setDeletePAFModalMode(ModalMode.active)
    analyticsServiceSingleton.trackEvent(AnalyticsEvent.CancelOrderMA, {
      orderId: orderId,
      programName: order?.lines[0].programName,
      programId: order?.lines[0].programId,
      location: 'Ma Checkout'
    })
  }

  const navigateToDashboard = () => {
    history.push('/patients')
  }

  // Saga-based delete confirmation handler with proper transaction management
  const handleDeletePAFConfirmation = async () => {
    setConfirmedNavigation(true)

    if (!order) {
      console.error('Cannot cancel order: order data is missing')
      return
    }

    setDeletePAFModalMode(ModalMode.submitting)

    try {
      // Prepare cancellation data with fallback resolution
      const orderItem = order.lines.find((x) => x.medicationFlag === 'Y')

      // Ensure all IDs are properly converted to strings
      const resolvedPhysicianId = String(
        physicianId || orderItem?.physician?.physicianId || ''
      ).trim()

      const resolvedPatientNumber = String(
        patientNumber || orderItem?.patientNumber || ''
      ).trim()

      const resolvedProgramId = String(
        programId || orderItem?.programId || ''
      ).trim()

      const resolvedPatientId = history.location?.state?.patientId

      // Resolve shipping address with multiple fallback sources
      const resolvedShippingAddressId = String(
        defaultShippingAddress?.addressId ||
          order.deliverTo?.addressId ||
          institute?.data?.shipTo?.find((addr) => addr.primaryFlag === 'Y')
            ?.addressId ||
          ''
      ).trim()

      if (
        !resolvedPhysicianId ||
        !resolvedPatientNumber ||
        !resolvedProgramId
      ) {
        const missingFields = []
        if (!resolvedPhysicianId) missingFields.push('physicianId')
        if (!resolvedPatientNumber) missingFields.push('patientNumber')
        if (!resolvedProgramId) missingFields.push('programId')

        console.error('Order cancellation data resolution failed:', {
          missingFields,
          orderNumber: order.orderNumber,
          stateValues: {
            physicianId,
            patientNumber,
            programId
          },
          orderItemValues: {
            physicianId: orderItem?.physician?.physicianId,
            patientNumber: orderItem?.patientNumber,
            programId: orderItem?.programId
          }
        })

        throw new Error(
          `Missing required data for order cancellation: ${missingFields.join(
            ', '
          )}. Please refresh the page and try again.`
        )
      }

      // Log warning if shipping address is missing
      if (!resolvedShippingAddressId) {
        console.warn(
          `Warning: No shipping address ID found for order ${order.orderNumber}. ` +
            `This may cause deleteOpa to fail.`
        )
      }

      // Create cancellation data using the saga helper
      const cancellationData =
        OrderCancellationSaga.createSimpleOrderCancellation(
          order.orderNumber,
          resolvedPhysicianId,
          resolvedPatientNumber,
          resolvedProgramId,
          resolvedShippingAddressId,
          patientOrderType || OPAOrderTypes.InitialOrder,
          'deleted from checkout',
          ''
        )

      // Add patient ID if available
      if (resolvedPatientId) {
        cancellationData.patientId = resolvedPatientId
      }

      // Execute the saga with monitoring utilities
      const transactionId = `order_${order.orderNumber}_${Date.now()}`
      const sagaLogger = createOrderCancellationLogger(transactionId)

      const saga = new OrderCancellationSaga(sagaLogger)
      const result = await saga.cancelOrder(cancellationData)

      // Log the result for monitoring
      logOrderCancellationResult(result)

      if (result.success && result.result) {
        const cancellationResult = result.result

        // Show success message based on what was actually completed
        let successMessage = 'Order cancellation completed successfully'
        if (cancellationResult.compensationRequired) {
          successMessage += ', but some compensation actions were required'
        }

        dispatch(
          createAnnounceEvent(
            AnnounceMode.Success,
            successMessage,
            'Order Cancelled',
            true
          )
        )

        // Set UI flags
        setNewFeature(NewFeatureElements.CancelCheckout)
        setIsSaved(true)

        // Track successful cancellation
        analyticsServiceSingleton.trackEvent(AnalyticsEvent.CancelOrderMA, {
          orderId: order.orderNumber,
          programId: resolvedProgramId,
          location: 'OPA Checkout',
          sagaExecutionTime: result.totalDuration,
          stepsCompleted: result.executedSteps.length,
          compensationRequired: cancellationResult.compensationRequired
        })

        // Navigate to dashboard after short delay to show success message
        setTimeout(() => {
          navigateToDashboard()
        }, 2000)
      } else {
        // Handle saga failure
        const errorMessage =
          result.error?.message || 'Order cancellation failed'
        console.error('Order cancellation saga failed:', result.error)

        // Check partial completion for better error messaging
        if (result.executedSteps.length > 0) {
          const completedSteps = result.executedSteps.filter(
            (step) => !step.error
          )
          const failedStep = result.executedSteps.find((step) => step.error)

          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `Order cancellation partially failed at step: ${failedStep?.stepDescription}. ${completedSteps.length} steps were completed and compensated. Please contact support.`,
              'Partial Cancellation Failure',
              false
            )
          )
        } else {
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `Order cancellation failed: ${errorMessage}`,
              'Cancellation Failed',
              false
            )
          )
        }

        setDeletePAFModalMode(ModalMode.hasError)
        setDeletePAFError(errorMessage)
      }
    } catch (error: any) {
      console.error('Error in order cancellation:', error)

      dispatch(
        createAnnounceEvent(
          AnnounceMode.Error,
          `Failed to initiate order cancellation: ${error.message}`,
          'Cancellation Error',
          false
        )
      )

      setDeletePAFModalMode(ModalMode.hasError)
      setDeletePAFError(error.message || 'Unknown error occurred')
    } finally {
      setIsCancelling(false)
    }
  }

  const handleDeletePAFClose = () => {
    setIsCancelling(false)
    setDeletePAFModalMode(undefined)
  }

  type Deps = [boolean, ProgramCatalogDto]
  useEffectOnlyOnce(
    (dependencies: Deps) => {
      analyticsServiceSingleton.trackPageView(
        AnalyticsPageEvent.ViewOPACheckout,
        {
          'Program name': dependencies[1].programName,
          'Program ID': dependencies[1].projectId
        }
      )
    },
    [isProgramLoading, program],
    (dependencies: Deps) => !dependencies[0] && dependencies[1]
  )

  useEffect(() => {
    if (institute) {
      const currentAddress = institute.data?.shipTo.find(
        (x) => x.addressId === deliveryData.shippingAddressId
      )
      if (currentAddress) {
        setSelectedAddress(currentAddress)
      }
    }

    return () => {}
  }, [deliveryData.shippingAddressId, institute])

  useEffect(() => {
    if (institute?.data) {
      setDeliveryAddresses(institute.data.shipTo.map((address) => address))
    }

    return () => {}
  }, [institute.data, institute])

  useEffect(() => {
    if (order) {
      const orderItem = order.lines.filter((x) => x.medicationFlag === 'Y')[0]
      if (orderItem?.physician) {
        let physicianName = orderItem.physician.physicianFullName
        if (orderItem.physician.physicianTitle) {
          physicianName = orderItem.physician?.physicianTitle.concat(
            ' ',
            orderItem.physician.physicianFullName
          )
        }
        setPhysicianTitleAndFullName(physicianName)
      }
    }
    return () => {}
  }, [order])

  const triggerDelayedError = (delay: number) => {
    setTimeout(() => {
      setErrorVisible(true)
    }, delay)
  }

  useEffect(() => {
    const unblock = history.block((location): any => {
      if (!confirmedNavigation) {
        if (location.pathname.includes('/opa-order-summary')) {
          return true
        }
        if (location.pathname === redirectUrl) {
          return true
        }
        if (!isPromptModalShown) {
          setIsPromptModalShown(true)
          setIsLogoClicked(false)
        } else {
          setShowExitPreventionModal(true)
        }
        setLastLocation(location)
        return false
      }
      return true
    })

    return () => {
      unblock()
    }
  }, [confirmedNavigation, isPromptModalShown])

  const handleCancelNavigation = () => {
    setShowExitPreventionModal(false)
    setConfirmedNavigation(false)
  }

  return order ? (
    <>
      <OpaExitPreventionPromptModal
        isOpen={isLogoClicked}
        handleClose={() => setIsLogoClicked(false)}
        handleOnConfirm={() => history.push(redirectUrl)}
      />
      <OpaExitPreventionModal
        isOpen={showExitPreventionModal}
        handleClose={handleCancelNavigation}
      />
      <DeletePAFModal
        isOpen={deletePAFModalMode !== undefined}
        isSubmitting={deletePAFModalMode === ModalMode.submitting}
        hasError={deletePAFModalMode === ModalMode.hasError}
        errorMessage={deletePAFError}
        isCancelling={isCancelling}
        handleConfirmation={handleDeletePAFConfirmation}
        handleClose={handleDeletePAFClose}
      />
      <CreateAddressModal
        addressModalError={addressModalError}
        isOpen={isNewShippingAddressOpen}
        handleFormSubmit={handleNewShippingAddress}
        handleClose={() => {
          setAddressModalError('')
          setIsNewShippingAddressOpen(false)
        }}
        isSubmitting={isSubmittingAddress}
        selectedCountry={portfolioCountryCode}
        countries={countries}
        hasError={!!addressModalError}
        resetForm={resetForm}
      />
      <OpaCheckout
        isLoading={
          isOrderLoading ||
          isProgramLoading ||
          !order ||
          isDefaultDetailsLoading
        }
        isSubmitting={isSubmitting}
        isSubmittingSaveAndClose={isSubmittingSaveAndClose}
        isSubmitted={isSubmitted}
        isErrorVisible={errorVisible}
        isSaved={isSaved}
        order={order}
        program={program}
        displayEditMode={displayEditMode}
        deliveryData={deliveryData}
        shippingAddress={
          selectedAddress?.addressId === deliveryData.shippingAddressId
            ? selectedAddress
            : deliveryAddresses.find(
                (address) =>
                  address.addressId === deliveryData.shippingAddressId
              ) || selectedAddress
        }
        deliveryAddresses={deliveryAddresses}
        deliveryNote={deliveryNote}
        newFeaturesList={newFeaturesList}
        noteZIndex={noteZIndex}
        buttonZIndex={buttonZIndex}
        checkboxShaddow={checkboxShaddow}
        oldAddressId={oldAddressId}
        setIsLogoClicked={setIsLogoClicked}
        displayNewFeature={displayNewFeature}
        isCancelling={isCancelling}
        handleSetOldAddressId={setOldAddressId}
        setDeliveryNote={setDeliveryNote}
        handleSetDeliveryData={setDeliveryData}
        handleSetDisplayMode={setDisplayEditMode}
        handleConfirmOrder={handleConfirmOrder}
        handleSetIsNewShippingAddressOpen={() =>
          setIsNewShippingAddressOpen(true)
        }
        handleDeliveryDataChange={handleDeliveryDataChange}
        handleSetShippingAddress={handleAddressChange}
        patientNumber={patientNumber!}
        physicianTitleAndFullName={physicianTitleAndFullName}
        programSponsor={program?.program?.sponsorName}
        patientDob={patientDOB}
        handleCancelButton={handleCancelButton}
        handleSaveAndCloseButton={handleSaveAndCloseButton}
        handleErrorModal={setShowExitPreventionModal}
        isDuplicatePoWarningOpen={isDuplicatePoModalOpen}
        duplicatePoData={duplicatePoData}
        isProcessingDuplicatePo={isProcessingDuplicatePo}
        handleDuplicatePoConfirm={handleDuplicatePoConfirm}
        handleDuplicatePoCancel={handleDuplicatePoCancel}
      />
    </>
  ) : (
    <Container
      style={{
        height: `calc(100vh - ${ClinTheme.componentsHeights.footer}px)`
      }}
    >
      <Row align="center">
        <Col>
          <StyledSpinnerContainer>
            <ClinSpinner size={ClinTheme.space[7]} />
          </StyledSpinnerContainer>
        </Col>
      </Row>
    </Container>
  )
}
